'use client';
import React, { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { X, Briefcase, DollarSign, Zap, BarChart2, FileText, ShieldCheck, Upload, AlertCircle, CheckCircle2, Tag, ImagePlus, Brain, TrendingUp, Users, Shield, ChevronDown, ChevronUp, Sparkles, Lightbulb, AlertTriangle, BookOpen, Search, HelpCircle, Star, PenLine, Copy } from 'lucide-react';
import { PROBLEM_CATEGORIES } from './ProblemFeed';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { useChat } from '@/lib/hooks/useChat';
import toast from 'react-hot-toast';

function wordCount(text: string): number {
  return text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
}

interface ProjectionRow {
  m6: string;
  m12: string;
  m18: string;
  m24: string;
}

interface EntrepreneurFormData {
  problem: string;
  category: string;
  solution: string;
  productService: string;
  marketSize: string;
  competition: string;
  fundingNeeded: string;
  pricePerShare: string;
  invest: string;
  revenue: ProjectionRow;
  expenses: ProjectionRow;
  profit: ProjectionRow;
  businessPlan: File | null;
  policeClearance: File | null;
  picture: File | null;
}

const EMPTY_PROJECTION: ProjectionRow = { m6: '', m12: '', m18: '', m24: '' };

const DEFAULT_FORM: EntrepreneurFormData = {
  problem: '',
  category: '',
  solution: '',
  productService: '',
  marketSize: '',
  competition: '',
  fundingNeeded: '',
  pricePerShare: '',
  invest: '',
  revenue: { ...EMPTY_PROJECTION },
  expenses: { ...EMPTY_PROJECTION },
  profit: { ...EMPTY_PROJECTION },
  businessPlan: null,
  policeClearance: null,
  picture: null,
};

interface EntrepreneurFormProps {
  onClose: () => void;
  onSubmit: (data: EntrepreneurFormData, savedSolution?: import('./mockData').Solution) => void;
  standalone?: boolean;
  initialProblem?: string;
  problemId?: string; // UUID of the problem being solved (optional)
}

interface AIAnalysis {
  revenueGeneration: string;
  competitionAnalysis: string;
  swot: {
    strengths: string[];
    weaknesses: string[];
    opportunities: string[];
    threats: string[];
  };
  ideaImprovement: {
    uniqueAngles: string[];
    revenueStreams: string[];
    targetSegments: string[];
    partnerships: string[];
    scalabilityOpportunities: string[];
  };
  riskAssessment: {
    risks: Array<{ risk: string; mitigation: string }>;
  };
  businessPlanSummary: string;
  marketAnalysis: {
    potentialCustomers: string;
    marketTrends: string;
    competitors: string;
    demandFactors: string;
    pricingStrategies: string;
  };
  questionsForEntrepreneur: string[];
  investorReadinessScore: {
    innovation: number;
    innovationNote: string;
    marketPotential: number;
    marketPotentialNote: string;
    scalability: number;
    scalabilityNote: string;
    financialClarity: number;
    financialClarityNote: string;
    overallScore: number;
    overallNote: string;
  };
  professionalRewrite: string;
  similarBusinesses: Array<{ name: string; description: string }>;
}

const MONTHS = ['6 months', '12 months', '18 months', '24 months'] as const;
const MONTH_KEYS: (keyof ProjectionRow)[] = ['m6', 'm12', 'm18', 'm24'];

export default function EntrepreneurForm({ onClose, onSubmit, standalone = false, initialProblem, problemId }: EntrepreneurFormProps) {
  const [form, setForm] = useState<EntrepreneurFormData>(() => ({
    ...DEFAULT_FORM,
    problem: initialProblem ?? '',
  }));
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [picturePreviewUrl, setPicturePreviewUrl] = useState<string | null>(null);
  const [aiAnalysis, setAiAnalysis] = useState<AIAnalysis | null>(null);
  const [aiExpanded, setAiExpanded] = useState(true);
  const businessPlanRef = useRef<HTMLInputElement>(null);
  const policeRef = useRef<HTMLInputElement>(null);
  const pictureRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const { user } = useAuth();

  const { response: aiResponse, isLoading: aiLoading, error: aiError, sendMessage: sendAiMessage } = useChat(
    'GEMINI',
    'gemini/gemini-2.5-flash',
    false
  );

  useEffect(() => {
    if (aiError) toast.error('AI analysis failed: ' + aiError.message);
  }, [aiError]);

  useEffect(() => {
    if (aiResponse && !aiLoading) {
      try {
        const jsonMatch = aiResponse.match(/```json\s*([\s\S]*?)\s*```/) || aiResponse.match(/```\s*([\s\S]*?)\s*```/) || aiResponse.match(/(\{[\s\S]*\})/);
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[1]);
          setAiAnalysis(parsed);
          setAiExpanded(true);
        } else {
          const parsed = JSON.parse(aiResponse.trim());
          setAiAnalysis(parsed);
          setAiExpanded(true);
        }
      } catch {
        setAiAnalysis({
          revenueGeneration: aiResponse.substring(0, 500),
          competitionAnalysis: '',
          swot: { strengths: [], weaknesses: [], opportunities: [], threats: [] },
          ideaImprovement: { uniqueAngles: [], revenueStreams: [], targetSegments: [], partnerships: [], scalabilityOpportunities: [] },
          riskAssessment: { risks: [] },
          businessPlanSummary: '',
          marketAnalysis: { potentialCustomers: '', marketTrends: '', competitors: '', demandFactors: '', pricingStrategies: '' },
          questionsForEntrepreneur: [],
          investorReadinessScore: { innovation: 0, innovationNote: '', marketPotential: 0, marketPotentialNote: '', scalability: 0, scalabilityNote: '', financialClarity: 0, financialClarityNote: '', overallScore: 0, overallNote: '' },
          professionalRewrite: '',
          similarBusinesses: [],
        });
        setAiExpanded(true);
      }
    }
  }, [aiResponse, aiLoading]);

  const problemWords = wordCount(form.problem);
  const solutionWords = wordCount(form.solution);

  function setField<K extends keyof EntrepreneurFormData>(key: K, value: EntrepreneurFormData[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key as string]) setErrors((prev) => ({ ...prev, [key]: '' }));
  }

  function setProjection(type: 'revenue' | 'expenses' | 'profit', key: keyof ProjectionRow, value: string) {
    setForm((prev) => {
      const updated = {
        ...prev,
        [type]: { ...prev[type], [key]: value },
      };
      // Auto-calculate profit = revenue - expenses
      if (type === 'revenue' || type === 'expenses') {
        const newRevenue = type === 'revenue' ? { ...prev.revenue, [key]: value } : prev.revenue;
        const newExpenses = type === 'expenses' ? { ...prev.expenses, [key]: value } : prev.expenses;
        const newProfit: ProjectionRow = { m6: '', m12: '', m18: '', m24: '' };
        MONTH_KEYS.forEach((k) => {
          const rev = parseFloat(newRevenue[k].replace(/[^0-9.-]/g, '')) || 0;
          const exp = parseFloat(newExpenses[k].replace(/[^0-9.-]/g, '')) || 0;
          newProfit[k] = (rev - exp) !== 0 ? String(rev - exp) : '';
        });
        updated.profit = newProfit;
      }
      return updated;
    });
  }

  function validate(): boolean {
    const errs: Partial<Record<string, string>> = {};
    if (!standalone) {
      if (!form.problem.trim()) errs.problem = 'Required.';
      else if (problemWords > 50) errs.problem = 'Max 50 words.';
    } else {
      if (form.problem.trim() && problemWords > 50) errs.problem = 'Max 50 words.';
    }
    if (!form.category) errs.category = 'Required.';
    if (!form.solution.trim()) errs.solution = 'Required.';
    else if (solutionWords > 50) errs.solution = 'Max 50 words.';
    if (!form.productService.trim()) errs.productService = 'Required.';
    if (!form.marketSize.trim()) errs.marketSize = 'Required.';
    if (!form.competition.trim()) errs.competition = 'Required.';
    if (!form.fundingNeeded.trim()) errs.fundingNeeded = 'Required.';
    if (!form.pricePerShare.trim()) errs.pricePerShare = 'Required.';
    if (!form.invest.trim()) errs.invest = 'Required.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handlePictureChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    if (file) {
      setField('picture', file);
      const url = URL.createObjectURL(file);
      setPicturePreviewUrl(url);
    }
  }

  function removePicture() {
    setField('picture', null);
    setPicturePreviewUrl(null);
    if (pictureRef.current) pictureRef.current.value = '';
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user) {
      router.push('/sign-up-login-screen');
      return;
    }
    if (!validate()) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const supabase = createClient();
      let imageUrl: string | null = null;

      if (form.picture) {
        const ext = form.picture.name.split('.').pop();
        const fileName = `${user.id}-${Date.now()}.${ext}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('solution-images')
          .upload(fileName, form.picture, { cacheControl: '3600', upsert: false });

        if (!uploadError && uploadData) {
          const { data: { publicUrl } } = supabase.storage
            .from('solution-images')
            .getPublicUrl(uploadData.path);
          imageUrl = publicUrl;
        }
        // If upload fails, continue without image (non-blocking)
      }

      // Determine which problem to attach this solution to.
      // If a problemId was passed as a prop, use it directly.
      // Otherwise, if we're not in standalone mode, try to find a matching problem by title.
      let resolvedProblemId: string | null = problemId ?? null;

      if (!resolvedProblemId && !standalone) {
        // Try to find a problem matching the entered problem text
        const { data: matchedProblems } = await supabase
          .from('problems')
          .select('id')
          .ilike('title', `%${form.problem.trim()}%`)
          .limit(1);
        resolvedProblemId = matchedProblems?.[0]?.id ?? null;
      }

      // If still no problem ID, we need a placeholder problem or skip DB insert for standalone
      // For standalone, create a generic problem or use the first available problem
      if (!resolvedProblemId) {
        const { data: firstProblem } = await supabase
          .from('problems')
          .select('id')
          .order('created_at', { ascending: false })
          .limit(1);
        resolvedProblemId = firstProblem?.[0]?.id ?? null;
      }

      let savedSolution: import('./mockData').Solution | undefined;

      if (resolvedProblemId) {
        const fundingAskNum = parseFloat(form.fundingNeeded.replace(/[^0-9.]/g, '')) || 0;
        const pricePerShareNum = parseFloat(form.pricePerShare.replace(/[^0-9.]/g, '')) || 0;

        const insertPayload = {
          problem_id: resolvedProblemId,
          user_id: user.id,
          title: form.solution.trim() || form.productService.trim() || 'Business Proposal',
          solution_status: 'proposed' as const,
          funding_ask: fundingAskNum,
          funding_offered: 0,
          rating: 0,
          rating_count: 0,
          excerpt: form.solution.trim(),
          tags: form.category ? [form.category] : [],
          image_url: imageUrl ?? '',
          image_alt: form.solution.trim() || 'Solution image',
          problem_description: form.problem.trim(),
          solution_description: form.solution.trim(),
          product_service: form.productService.trim(),
          market_size: form.marketSize.trim(),
          competition: form.competition.trim(),
          funding_needed: fundingAskNum,
          price_per_share: pricePerShareNum,
          revenue_projections: form.revenue,
          expenses_projections: form.expenses,
          profit_projections: form.profit,
        };

        const { data: insertedRow, error: insertError } = await supabase
          .from('solutions')
          .insert(insertPayload)
          .select('*, user_profiles(full_name, reputation)')
          .single();

        if (insertError) {
          console.error('Failed to save solution:', insertError);
          setSubmitError('Failed to save your proposal. Please try again.');
          setSubmitting(false);
          return;
        }

        if (insertedRow) {
          savedSolution = {
            id: insertedRow.id,
            title: insertedRow.title,
            entrepreneur: insertedRow.user_profiles?.full_name || user.email || 'You',
            entrepreneurReputation: insertedRow.user_profiles?.reputation ?? 50,
            status: insertedRow.solution_status,
            fundingAsk: Number(insertedRow.funding_ask) || 0,
            fundingOffered: Number(insertedRow.funding_offered) || 0,
            rating: Number(insertedRow.rating) || 0,
            ratingCount: insertedRow.rating_count || 0,
            excerpt: insertedRow.excerpt || '',
            tags: insertedRow.tags || [],
            submittedAt: new Date(insertedRow.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
            fundingOffers: [],
            image: insertedRow.image_url || undefined,
            imageAlt: insertedRow.image_alt || '',
            userId: insertedRow.user_id,
            problem: insertedRow.problem_description || '',
            solution: insertedRow.solution_description || '',
            productService: insertedRow.product_service || '',
            marketSize: insertedRow.market_size || '',
            competition: insertedRow.competition || '',
            fundingNeeded: String(insertedRow.funding_needed || ''),
            pricePerShare: String(insertedRow.price_per_share || ''),
            invest: form.invest,
            revenue: insertedRow.revenue_projections || form.revenue,
            expenses: insertedRow.expenses_projections || form.expenses,
            profit: insertedRow.profit_projections || form.profit,
            entrepreneurVerified: insertedRow.entrepreneur_verified ?? false,
          };
        }
      } else {
        // No problem exists yet — inform user
        setSubmitError('No problem found to attach your solution to. Please post a problem first.');
        setSubmitting(false);
        return;
      }

      onSubmit({ ...form, picture: form.picture }, savedSolution);
      setSubmitted(true);
    } catch (err: any) {
      console.error('Unexpected error:', err);
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  function handleReset() {
    setForm(DEFAULT_FORM);
    setErrors({});
    setSubmitted(false);
    setSubmitError(null);
    setPicturePreviewUrl(null);
  }

  function handleRunAiAnalysis() {
    const { problem, solution, productService, marketSize, competition, category, fundingNeeded, revenue } = form;
    if (!solution.trim() && !productService.trim()) {
      toast.error('Please fill in at least the Solution and Product/Service fields before running AI analysis.');
      return;
    }

    const prompt = `You are an expert business analyst specializing in African markets and startup ecosystems. Analyze the following business proposal and return a JSON object with this EXACT structure (no extra text, only valid JSON):

{
  "revenueGeneration": "A detailed paragraph predicting revenue generation potential, key revenue streams, and realistic financial outlook.",
  "competitionAnalysis": "A detailed paragraph analyzing the competitive landscape, key competitors, differentiation opportunities, and market positioning.",
  "swot": {
    "strengths": ["strength 1", "strength 2", "strength 3"],
    "weaknesses": ["weakness 1", "weakness 2", "weakness 3"],
    "opportunities": ["opportunity 1", "opportunity 2", "opportunity 3"],
    "threats": ["threat 1", "threat 2", "threat 3"]
  },
  "ideaImprovement": {
    "uniqueAngles": ["unique angle 1", "unique angle 2"],
    "revenueStreams": ["additional revenue stream 1", "additional revenue stream 2"],
    "targetSegments": ["target segment 1", "target segment 2"],
    "partnerships": ["potential partner 1", "potential partner 2"],
    "scalabilityOpportunities": ["scalability opportunity 1", "scalability opportunity 2"]
  },
  "riskAssessment": {
    "risks": [
      { "risk": "Risk description", "mitigation": "How to reduce this risk" },
      { "risk": "Risk description 2", "mitigation": "How to reduce this risk 2" }
    ]
  },
  "businessPlanSummary": "A concise, professional 3-5 sentence business plan summary that investors can quickly review.",
  "marketAnalysis": {
    "potentialCustomers": "Description of potential customer base",
    "marketTrends": "Relevant market trends",
    "competitors": "Key competitors overview",
    "demandFactors": "Factors driving demand",
    "pricingStrategies": "Suitable pricing strategies"
  },
  "questionsForEntrepreneur": ["Question 1?", "Question 2?", "Question 3?"],
  "investorReadinessScore": {
    "innovation": 8,
    "innovationNote": "Explanation for innovation score",
    "marketPotential": 7,
    "marketPotentialNote": "Explanation for market potential score",
    "scalability": 6,
    "scalabilityNote": "Explanation for scalability score",
    "financialClarity": 5,
    "financialClarityNote": "Explanation for financial clarity score",
    "overallScore": 65,
    "overallNote": "Overall investor readiness assessment"
  },
  "professionalRewrite": "A professionally rewritten, persuasive, and well-organized version of the business proposal that is grammatically correct and easy for investors to understand.",
  "similarBusinesses": [
    { "name": "Similar Business Name", "description": "How it relates and what the entrepreneur can learn from it" }
  ]
}

Business Proposal Details:
- Problem: ${problem || 'Not specified'}
- Category: ${category || 'Not specified'}
- Solution: ${solution || 'Not specified'}
- Product/Service: ${productService || 'Not specified'}
- Market Size: ${marketSize || 'Not specified'}
- Competition: ${competition || 'Not specified'}
- Funding Needed: $${fundingNeeded || 'Not specified'}
- Revenue Projections (6/12/18/24 months): $${revenue.m6 || 0} / $${revenue.m12 || 0} / $${revenue.m18 || 0} / $${revenue.m24 || 0}

Return ONLY the JSON object, no markdown, no extra text.`;

    setAiAnalysis(null);
    sendAiMessage([
      { role: 'system', content: 'You are an expert business analyst specializing in African markets and startup ecosystems. Always respond with valid JSON only, no markdown code blocks, no extra text.' },
      { role: 'user', content: prompt },
    ], { temperature: 0.7, max_tokens: 4000 });
  }

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center gap-4">
        <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center">
          <CheckCircle2 size={32} className="text-success" />
        </div>
        <h3 className="text-lg font-bold text-foreground">
          {standalone ? 'Solution Posted!' : 'Business Plan Submitted!'}
        </h3>
        <p className="text-sm text-muted-foreground max-w-xs">
          {standalone
            ? 'Your solution has been posted successfully. Investors and the community can now discover and fund it.' :'Your entrepreneur proposal has been submitted successfully. Investors will be able to review it shortly.'}
        </p>
        <div className="flex gap-3 mt-2">
          <button
            onClick={handleReset}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
          >
            Submit Another
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-border text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-0">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-card sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Briefcase size={16} className="text-primary" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-foreground">
              {standalone ? 'Post a Solution' : 'Entrepreneur Proposal'}
            </h2>
            <p className="text-xs text-muted-foreground">
              {standalone ? 'Share your solution — no problem required' : 'Submit your business solution'}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-lg hover:bg-muted transition-colors"
          aria-label="Close form"
        >
          <X size={16} className="text-muted-foreground" />
        </button>
      </div>

      <div className="px-5 py-5 space-y-5 overflow-y-auto">

        {/* ── Section 1: Problem ── */}
        <Section icon={<AlertCircle size={14} className="text-danger" />} title="Problem" subtitle={standalone ? "Optionally describe the problem your solution addresses" : "Describe the problem you're solving"}>
          <div className="space-y-3">
            <div>
              <textarea
                value={form.problem}
                onChange={(e) => setField('problem', e.target.value)}
                rows={4}
                placeholder={standalone ? "Optionally describe the problem your solution addresses..." : "Describe the core problem in clear, concise terms..."}
                className={`w-full px-3 py-2.5 rounded-xl border text-sm text-foreground bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all ${errors.problem ? 'border-danger' : 'border-border'}`}
              />
              <div className="flex justify-between mt-1">
                {errors.problem ? (
                  <p className="text-xs text-danger">{errors.problem}</p>
                ) : standalone ? (
                  <p className="text-xs text-muted-foreground">Optional — leave blank to post a standalone solution</p>
                ) : (
                  <span />
                )}
                <span className={`text-xs ml-auto ${problemWords > 50 ? 'text-danger font-semibold' : 'text-muted-foreground'}`}>
                  {problemWords}/50 words
                </span>
              </div>
            </div>

            {/* Category dropdown */}
            <FormField label="Problem Category" required error={errors.category}>
              <div className="relative">
                <Tag size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <select
                  value={form.category}
                  onChange={(e) => setField('category', e.target.value)}
                  className={`w-full pl-8 pr-3 py-2 rounded-lg border text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all appearance-none ${errors.category ? 'border-danger' : 'border-border'}`}
                >
                  <option value="">Select a category...</option>
                  {PROBLEM_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </FormField>
          </div>
        </Section>

        {/* ── Section 2: Solution ── */}
        <Section icon={<Zap size={14} className="text-warning" />} title="Solution" subtitle="How do you solve this problem?">
          <div>
            <textarea
              value={form.solution}
              onChange={(e) => setField('solution', e.target.value)}
              rows={4}
              placeholder="Describe your proposed solution concisely..."
              className={`w-full px-3 py-2.5 rounded-xl border text-sm text-foreground bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-all ${errors.solution ? 'border-danger' : 'border-border'}`}
            />
            <div className="flex justify-between mt-1">
              {errors.solution ? (
                <p className="text-xs text-danger">{errors.solution}</p>
              ) : (
                <span />
              )}
              <span className={`text-xs ml-auto ${solutionWords > 50 ? 'text-danger font-semibold' : 'text-muted-foreground'}`}>
                {solutionWords}/50 words
              </span>
            </div>
          </div>
        </Section>

        {/* ── Section 3 & 4: Product/Service + Market Size ── */}
        <Section icon={<Briefcase size={14} className="text-primary" />} title="Business Overview">
          <div className="space-y-3">
            <FormField label="Product / Service" required error={errors.productService}>
              <input
                type="text"
                value={form.productService}
                onChange={(e) => setField('productService', e.target.value)}
                placeholder="e.g. SaaS platform, mobile app, physical product..."
                className={inputClass(!!errors.productService)}
              />
            </FormField>
            <FormField label="Market Size" required error={errors.marketSize}>
              <input
                type="text"
                value={form.marketSize}
                onChange={(e) => setField('marketSize', e.target.value)}
                placeholder="e.g. $2.5B TAM, 500K potential customers..."
                className={inputClass(!!errors.marketSize)}
              />
            </FormField>
            <FormField label="Competition" required error={errors.competition}>
              <input
                type="text"
                value={form.competition}
                onChange={(e) => setField('competition', e.target.value)}
                placeholder="e.g. Direct competitors, indirect alternatives..."
                className={inputClass(!!errors.competition)}
              />
            </FormField>
          </div>
        </Section>

        {/* ── AI Analysis Section ── */}
        <div className="bg-card border border-primary/30 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-primary/20 bg-primary/5">
            <div className="flex items-center gap-2">
              <Brain size={14} className="text-primary" />
              <div>
                <p className="text-xs font-bold text-foreground">AI Business Analysis</p>
                <p className="text-xs text-muted-foreground">Powered by Gemini — Full Proposal Intelligence</p>
              </div>
            </div>
            {aiAnalysis && (
              <button
                type="button"
                onClick={() => setAiExpanded((v) => !v)}
                className="p-1 rounded hover:bg-primary/10 transition-colors"
                aria-label={aiExpanded ? 'Collapse' : 'Expand'}
              >
                {aiExpanded ? <ChevronUp size={14} className="text-primary" /> : <ChevronDown size={14} className="text-primary" />}
              </button>
            )}
          </div>
          <div className="p-4">
            {!aiAnalysis && !aiLoading && (
              <div className="flex flex-col items-center gap-3 py-4 text-center">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Sparkles size={18} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">Get Full AI-Powered Business Intelligence</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Fill in your business details above, then let Gemini generate a complete analysis: SWOT, risks, market insights, investor score, and more.</p>
                </div>
                <button
                  type="button"
                  onClick={handleRunAiAnalysis}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
                >
                  <Brain size={14} />
                  Analyze with Gemini AI
                </button>
              </div>
            )}

            {aiLoading && (
              <div className="flex flex-col items-center gap-3 py-6 text-center">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center animate-pulse">
                  <Brain size={18} className="text-primary" />
                </div>
                <p className="text-sm font-semibold text-foreground">Analyzing your business...</p>
                <p className="text-xs text-muted-foreground">Gemini is generating your full business intelligence report.</p>
                <div className="flex gap-1 mt-1">
                  <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}

            {aiAnalysis && aiExpanded && (
              <div className="space-y-4">

                {/* ── 1. Idea Improvement ── */}
                {aiAnalysis.ideaImprovement && (
                  <div className="rounded-xl border border-purple-200 bg-purple-50 dark:bg-purple-950/20 dark:border-purple-800 p-3">
                    <div className="flex items-center gap-2 mb-3">
                      <Lightbulb size={13} className="text-purple-600 dark:text-purple-400" />
                      <p className="text-xs font-bold text-purple-700 dark:text-purple-300">Idea Improvement Suggestions</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {aiAnalysis.ideaImprovement.uniqueAngles?.length > 0 && (
                        <IdeaGroup label="✨ Unique Angles" items={aiAnalysis.ideaImprovement.uniqueAngles} color="purple" />
                      )}
                      {aiAnalysis.ideaImprovement.revenueStreams?.length > 0 && (
                        <IdeaGroup label="💰 Additional Revenue Streams" items={aiAnalysis.ideaImprovement.revenueStreams} color="purple" />
                      )}
                      {aiAnalysis.ideaImprovement.targetSegments?.length > 0 && (
                        <IdeaGroup label="🎯 Target Segments" items={aiAnalysis.ideaImprovement.targetSegments} color="purple" />
                      )}
                      {aiAnalysis.ideaImprovement.partnerships?.length > 0 && (
                        <IdeaGroup label="🤝 Potential Partnerships" items={aiAnalysis.ideaImprovement.partnerships} color="purple" />
                      )}
                      {aiAnalysis.ideaImprovement.scalabilityOpportunities?.length > 0 && (
                        <IdeaGroup label="📈 Scalability Opportunities" items={aiAnalysis.ideaImprovement.scalabilityOpportunities} color="purple" />
                      )}
                    </div>
                  </div>
                )}

                {/* ── 2. Revenue Generation ── */}
                {aiAnalysis.revenueGeneration && (
                  <div className="rounded-xl border border-success/20 bg-success/5 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp size={13} className="text-success" />
                      <p className="text-xs font-bold text-success">Revenue Generation Prediction</p>
                    </div>
                    <p className="text-xs text-foreground leading-relaxed">{aiAnalysis.revenueGeneration}</p>
                  </div>
                )}

                {/* ── 3. SWOT Analysis ── */}
                {(aiAnalysis.swot?.strengths?.length > 0 || aiAnalysis.swot?.weaknesses?.length > 0 || aiAnalysis.swot?.opportunities?.length > 0 || aiAnalysis.swot?.threats?.length > 0) && (
                  <div className="rounded-xl border border-border p-3">
                    <div className="flex items-center gap-2 mb-3">
                      <Shield size={13} className="text-primary" />
                      <p className="text-xs font-bold text-foreground">SWOT Analysis</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {aiAnalysis.swot.strengths?.length > 0 && (
                        <div className="rounded-lg bg-success/5 border border-success/20 p-2.5">
                          <p className="text-xs font-bold text-success mb-1.5">💪 Strengths</p>
                          <ul className="space-y-1">
                            {aiAnalysis.swot.strengths.map((s, i) => (
                              <li key={i} className="text-xs text-foreground flex gap-1.5"><span className="text-success mt-0.5">•</span>{s}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {aiAnalysis.swot.weaknesses?.length > 0 && (
                        <div className="rounded-lg bg-danger/5 border border-danger/20 p-2.5">
                          <p className="text-xs font-bold text-danger mb-1.5">⚠️ Weaknesses</p>
                          <ul className="space-y-1">
                            {aiAnalysis.swot.weaknesses.map((w, i) => (
                              <li key={i} className="text-xs text-foreground flex gap-1.5"><span className="text-danger mt-0.5">•</span>{w}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {aiAnalysis.swot.opportunities?.length > 0 && (
                        <div className="rounded-lg bg-info/5 border border-info/20 p-2.5">
                          <p className="text-xs font-bold text-info mb-1.5">🚀 Opportunities</p>
                          <ul className="space-y-1">
                            {aiAnalysis.swot.opportunities.map((o, i) => (
                              <li key={i} className="text-xs text-foreground flex gap-1.5"><span className="text-info mt-0.5">•</span>{o}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {aiAnalysis.swot.threats?.length > 0 && (
                        <div className="rounded-lg bg-warning/5 border border-warning/20 p-2.5">
                          <p className="text-xs font-bold text-warning mb-1.5">🛡️ Threats</p>
                          <ul className="space-y-1">
                            {aiAnalysis.swot.threats.map((t, i) => (
                              <li key={i} className="text-xs text-foreground flex gap-1.5"><span className="text-warning mt-0.5">•</span>{t}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── 4. Risk Assessment ── */}
                {aiAnalysis.riskAssessment?.risks?.length > 0 && (
                  <div className="rounded-xl border border-danger/20 bg-danger/5 p-3">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle size={13} className="text-danger" />
                      <p className="text-xs font-bold text-danger">Risk Assessment</p>
                    </div>
                    <div className="space-y-2">
                      {aiAnalysis.riskAssessment.risks.map((item, i) => (
                        <div key={i} className="rounded-lg bg-background border border-danger/10 p-2.5">
                          <p className="text-xs font-semibold text-foreground mb-1">⚠️ {item.risk}</p>
                          <p className="text-xs text-muted-foreground">🛡️ <span className="font-medium text-success">Mitigation:</span> {item.mitigation}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── 5. Business Plan Summary ── */}
                {aiAnalysis.businessPlanSummary && (
                  <div className="rounded-xl border border-primary/20 bg-primary/5 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <BookOpen size={13} className="text-primary" />
                      <p className="text-xs font-bold text-primary">Business Plan Summary</p>
                    </div>
                    <p className="text-xs text-foreground leading-relaxed">{aiAnalysis.businessPlanSummary}</p>
                  </div>
                )}

                {/* ── 6. Market Analysis ── */}
                {aiAnalysis.marketAnalysis && (
                  <div className="rounded-xl border border-info/20 bg-info/5 p-3">
                    <div className="flex items-center gap-2 mb-3">
                      <Search size={13} className="text-info" />
                      <p className="text-xs font-bold text-info">Market Analysis</p>
                    </div>
                    <div className="space-y-2">
                      {aiAnalysis.marketAnalysis.potentialCustomers && (
                        <MarketRow label="👥 Potential Customers" value={aiAnalysis.marketAnalysis.potentialCustomers} />
                      )}
                      {aiAnalysis.marketAnalysis.marketTrends && (
                        <MarketRow label="📊 Market Trends" value={aiAnalysis.marketAnalysis.marketTrends} />
                      )}
                      {aiAnalysis.marketAnalysis.competitors && (
                        <MarketRow label="🏆 Competitors" value={aiAnalysis.marketAnalysis.competitors} />
                      )}
                      {aiAnalysis.marketAnalysis.demandFactors && (
                        <MarketRow label="📈 Demand Factors" value={aiAnalysis.marketAnalysis.demandFactors} />
                      )}
                      {aiAnalysis.marketAnalysis.pricingStrategies && (
                        <MarketRow label="💲 Pricing Strategies" value={aiAnalysis.marketAnalysis.pricingStrategies} />
                      )}
                    </div>
                  </div>
                )}

                {/* ── 7. Competition Analysis ── */}
                {aiAnalysis.competitionAnalysis && (
                  <div className="rounded-xl border border-warning/20 bg-warning/5 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Users size={13} className="text-warning" />
                      <p className="text-xs font-bold text-warning">Competition Analysis</p>
                    </div>
                    <p className="text-xs text-foreground leading-relaxed">{aiAnalysis.competitionAnalysis}</p>
                  </div>
                )}

                {/* ── 8. Questions for Entrepreneur ── */}
                {aiAnalysis.questionsForEntrepreneur?.length > 0 && (
                  <div className="rounded-xl border border-orange-200 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-800 p-3">
                    <div className="flex items-center gap-2 mb-3">
                      <HelpCircle size={13} className="text-orange-600 dark:text-orange-400" />
                      <p className="text-xs font-bold text-orange-700 dark:text-orange-300">Questions to Address</p>
                      <span className="text-xs text-orange-500 dark:text-orange-400 ml-auto">Complete your proposal</span>
                    </div>
                    <ul className="space-y-1.5">
                      {aiAnalysis.questionsForEntrepreneur.map((q, i) => (
                        <li key={i} className="flex gap-2 text-xs text-foreground">
                          <span className="text-orange-500 font-bold shrink-0">{i + 1}.</span>
                          <span>{q}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* ── 9. Investor Readiness Score ── */}
                {aiAnalysis.investorReadinessScore && aiAnalysis.investorReadinessScore.overallScore > 0 && (
                  <div className="rounded-xl border border-border p-3">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Star size={13} className="text-yellow-500" />
                        <p className="text-xs font-bold text-foreground">Investor Readiness Score</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className={`text-lg font-black ${aiAnalysis.investorReadinessScore.overallScore >= 70 ? 'text-success' : aiAnalysis.investorReadinessScore.overallScore >= 50 ? 'text-warning' : 'text-danger'}`}>
                          {aiAnalysis.investorReadinessScore.overallScore}
                        </div>
                        <span className="text-xs text-muted-foreground">/100</span>
                      </div>
                    </div>
                    <div className="space-y-2 mb-3">
                      <ScoreBar label="Innovation" score={aiAnalysis.investorReadinessScore.innovation} note={aiAnalysis.investorReadinessScore.innovationNote} />
                      <ScoreBar label="Market Potential" score={aiAnalysis.investorReadinessScore.marketPotential} note={aiAnalysis.investorReadinessScore.marketPotentialNote} />
                      <ScoreBar label="Scalability" score={aiAnalysis.investorReadinessScore.scalability} note={aiAnalysis.investorReadinessScore.scalabilityNote} />
                      <ScoreBar label="Financial Clarity" score={aiAnalysis.investorReadinessScore.financialClarity} note={aiAnalysis.investorReadinessScore.financialClarityNote} />
                    </div>
                    {aiAnalysis.investorReadinessScore.overallNote && (
                      <p className="text-xs text-muted-foreground bg-muted/40 rounded-lg p-2 leading-relaxed">{aiAnalysis.investorReadinessScore.overallNote}</p>
                    )}
                  </div>
                )}

                {/* ── 10. Professional Rewrite ── */}
                {aiAnalysis.professionalRewrite && (
                  <div className="rounded-xl border border-teal-200 bg-teal-50 dark:bg-teal-950/20 dark:border-teal-800 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <PenLine size={13} className="text-teal-600 dark:text-teal-400" />
                        <p className="text-xs font-bold text-teal-700 dark:text-teal-300">Professional Rewrite</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard?.writeText(aiAnalysis.professionalRewrite);
                          toast.success('Copied to clipboard!');
                        }}
                        className="flex items-center gap-1 text-xs text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-200 transition-colors"
                      >
                        <Copy size={11} />
                        Copy
                      </button>
                    </div>
                    <p className="text-xs text-foreground leading-relaxed whitespace-pre-line">{aiAnalysis.professionalRewrite}</p>
                  </div>
                )}

                {/* ── 11. Similar Businesses ── */}
                {aiAnalysis.similarBusinesses?.length > 0 && (
                  <div className="rounded-xl border border-indigo-200 bg-indigo-50 dark:bg-indigo-950/20 dark:border-indigo-800 p-3">
                    <div className="flex items-center gap-2 mb-3">
                      <Briefcase size={13} className="text-indigo-600 dark:text-indigo-400" />
                      <p className="text-xs font-bold text-indigo-700 dark:text-indigo-300">Similar Businesses to Learn From</p>
                    </div>
                    <div className="space-y-2">
                      {aiAnalysis.similarBusinesses.map((biz, i) => (
                        <div key={i} className="rounded-lg bg-background border border-indigo-100 dark:border-indigo-800 p-2.5">
                          <p className="text-xs font-semibold text-foreground mb-0.5">🏢 {biz.name}</p>
                          <p className="text-xs text-muted-foreground">{biz.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Re-analyze button */}
                <button
                  type="button"
                  onClick={handleRunAiAnalysis}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-primary/30 text-primary text-xs font-semibold hover:bg-primary/5 transition-colors"
                >
                  <Brain size={12} />
                  Re-analyze with updated data
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Section 5: Funding ── */}
        <Section icon={<DollarSign size={14} className="text-success" />} title="Funding & Investment">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <FormField label="Funding Needed" required error={errors.fundingNeeded}>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <input
                  type="text"
                  value={form.fundingNeeded}
                  onChange={(e) => setField('fundingNeeded', e.target.value)}
                  placeholder="500,000"
                  className={`${inputClass(!!errors.fundingNeeded)} pl-7`}
                />
              </div>
            </FormField>
            <FormField label="Price / Share" required error={errors.pricePerShare}>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <input
                  type="text"
                  value={form.pricePerShare}
                  onChange={(e) => setField('pricePerShare', e.target.value)}
                  placeholder="10.00"
                  className={`${inputClass(!!errors.pricePerShare)} pl-7`}
                />
              </div>
            </FormField>
            <FormField label="Invest (Min)" required error={errors.invest}>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <input
                  type="text"
                  value={form.invest}
                  onChange={(e) => setField('invest', e.target.value)}
                  placeholder="1,000"
                  className={`${inputClass(!!errors.invest)} pl-7`}
                />
              </div>
            </FormField>
          </div>
        </Section>

        {/* ── Section 6: Financial Projections ── */}
        <Section icon={<BarChart2 size={14} className="text-info" />} title="Financial Projections" subtitle="Enter projected figures in USD ($)">
          <div className="space-y-4">
            <ProjectionTable
              label="Revenue Projections"
              colorClass="text-success"
              bgClass="bg-success/5 border-success/20"
              values={form.revenue}
              onChange={(key, val) => setProjection('revenue', key, val)}
            />
            <ProjectionTable
              label="Expense Projections"
              colorClass="text-danger"
              bgClass="bg-danger/5 border-danger/20"
              values={form.expenses}
              onChange={(key, val) => setProjection('expenses', key, val)}
            />
            <ProjectionTable
              label="Profit Projections"
              colorClass="text-primary"
              bgClass="bg-primary/5 border-primary/20"
              values={form.profit}
              onChange={(key, val) => setProjection('profit', key, val)}
              readOnly
            />
          </div>
        </Section>

        {/* ── Section 7: Documents (Optional) ── */}
        <Section icon={<FileText size={14} className="text-muted-foreground" />} title="Supporting Documents" subtitle="Optional — helps build investor confidence">
          <div className="space-y-3">
            {/* Picture Upload */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground block mb-1.5">
                Solution Image <span className="text-xs font-normal text-muted-foreground/60">(optional)</span>
              </label>
              <input
                ref={pictureRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handlePictureChange}
              />
              {picturePreviewUrl ? (
                <div className="relative rounded-xl overflow-hidden border border-border">
                  <img
                    src={picturePreviewUrl}
                    alt="Solution preview"
                    className="w-full h-40 object-cover"
                  />
                  <button
                    type="button"
                    onClick={removePicture}
                    className="absolute top-2 right-2 p-1 rounded-full bg-black/60 hover:bg-black/80 transition-colors"
                    aria-label="Remove image"
                  >
                    <X size={14} className="text-white" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => pictureRef.current?.click()}
                  className="w-full flex flex-col items-center justify-center gap-2 px-3 py-5 rounded-xl border border-dashed border-border hover:border-primary hover:bg-primary/5 transition-all group"
                >
                  <ImagePlus size={20} className="text-muted-foreground group-hover:text-primary transition-colors" />
                  <span className="text-xs text-muted-foreground group-hover:text-primary transition-colors">
                    Click to upload a solution image
                  </span>
                  <span className="text-xs text-muted-foreground/60">JPG, PNG, GIF, WebP · max 10 MB</span>
                </button>
              )}
            </div>

            <FileUploadField
              label="Business Plan"
              icon={<FileText size={14} className="text-primary" />}
              file={form.businessPlan}
              inputRef={businessPlanRef}
              accept=".pdf,.doc,.docx,.ppt,.pptx"
              onSelect={(f) => setField('businessPlan', f)}
              onClear={() => setField('businessPlan', null)}
            />
            <FileUploadField
              label="Police Clearance"
              icon={<ShieldCheck size={14} className="text-success" />}
              file={form.policeClearance}
              inputRef={policeRef}
              accept=".pdf,.jpg,.jpeg,.png"
              onSelect={(f) => setField('policeClearance', f)}
              onClear={() => setField('policeClearance', null)}
            />
          </div>
        </Section>

        {/* ── Submit ── */}
        <div className="pt-2 pb-4">
          {submitError && (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-danger/10 border border-danger/20 mb-3">
              <AlertCircle size={14} className="text-danger shrink-0" />
              <p className="text-xs text-danger">{submitError}</p>
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:bg-primary/90 active:scale-[0.98] transition-all shadow-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Submitting...
              </>
            ) : (
              'Submit Business Proposal'
            )}
          </button>
          <p className="text-xs text-muted-foreground text-center mt-2">
            By submitting, you confirm all information is accurate and truthful.
          </p>
        </div>
      </div>
    </form>
  );
}

/* ── Helper: Section wrapper ── */
function Section({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/30">
        {icon}
        <div>
          <p className="text-xs font-bold text-foreground">{title}</p>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

/* ── Helper: FormField ── */
function FormField({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-xs font-semibold text-muted-foreground block mb-1">
        {label} {required && <span className="text-danger">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-danger mt-0.5">{error}</p>}
    </div>
  );
}

/* ── Helper: input class ── */
function inputClass(hasError: boolean) {
  return `w-full px-3 py-2 rounded-lg border text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all ${hasError ? 'border-danger' : 'border-border'}`;
}

/* ── Helper: ProjectionTable ── */
function ProjectionTable({
  label,
  colorClass,
  bgClass,
  values,
  onChange,
  readOnly,
}: {
  label: string;
  colorClass: string;
  bgClass: string;
  values: ProjectionRow;
  onChange: (key: keyof ProjectionRow, value: string) => void;
  readOnly?: boolean;
}) {
  return (
    <div className={`rounded-xl border p-3 ${bgClass}`}>
      <div className="flex items-center justify-between mb-2.5">
        <p className={`text-xs font-bold ${colorClass}`}>{label}</p>
        {readOnly && (
          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">Auto-calculated</span>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {MONTH_KEYS.map((key, i) => (
          <div key={key}>
            <label className="text-xs text-muted-foreground block mb-1">{MONTHS[i]}</label>
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">$</span>
              <input
                type="text"
                value={values[key]}
                onChange={(e) => !readOnly && onChange(key, e.target.value)}
                readOnly={readOnly}
                placeholder="0"
                className={`w-full pl-5 pr-2 py-1.5 rounded-lg border text-xs text-foreground focus:outline-none transition-all ${
                  readOnly
                    ? 'border-border bg-muted/40 cursor-default select-none' :'border-border bg-background focus:ring-1 focus:ring-primary'
                }`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Helper: FileUploadField ── */
function FileUploadField({
  label,
  icon,
  file,
  inputRef,
  accept,
  onSelect,
  onClear,
}: {
  label: string;
  icon: React.ReactNode;
  file: File | null;
  inputRef: React.RefObject<HTMLInputElement | null>;
  accept: string;
  onSelect: (f: File) => void;
  onClear: () => void;
}) {
  return (
    <div>
      <label className="text-xs font-semibold text-muted-foreground block mb-1.5">
        {label} <span className="text-xs font-normal text-muted-foreground/60">(optional)</span>
      </label>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onSelect(f);
        }}
      />
      {file ? (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border bg-muted/40">
          {icon}
          <span className="text-xs text-foreground flex-1 truncate">{file.name}</span>
          <span className="text-xs text-muted-foreground shrink-0">
            {(file.size / 1024).toFixed(0)} KB
          </span>
          <button
            type="button"
            onClick={onClear}
            className="p-0.5 rounded hover:bg-muted transition-colors"
            aria-label="Remove file"
          >
            <X size={12} className="text-muted-foreground" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl border border-dashed border-border hover:border-primary hover:bg-primary/5 transition-all text-left group"
        >
          <Upload size={14} className="text-muted-foreground group-hover:text-primary transition-colors" />
          <span className="text-xs text-muted-foreground group-hover:text-primary transition-colors">
            Click to upload {label.toLowerCase()}
          </span>
        </button>
      )}
    </div>
  );
}

/* ── Helper: IdeaGroup ── */
function IdeaGroup({ label, items, color }: { label: string; items: string[]; color: string }) {
  return (
    <div className={`rounded-lg bg-background border border-${color}-100 dark:border-${color}-800 p-2.5`}>
      <p className="text-xs font-semibold text-foreground mb-1.5">{label}</p>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="text-xs text-muted-foreground flex gap-1.5">
            <span className="text-purple-500 mt-0.5 shrink-0">•</span>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Helper: MarketRow ── */
function MarketRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-background border border-info/10 p-2.5">
      <p className="text-xs font-semibold text-foreground mb-0.5">{label}</p>
      <p className="text-xs text-muted-foreground leading-relaxed">{value}</p>
    </div>
  );
}

/* ── Helper: ScoreBar ── */
function ScoreBar({ label, score, note }: { label: string; score: number; note: string }) {
  const pct = Math.min(Math.max((score / 10) * 100, 0), 100);
  const color = score >= 7 ? 'bg-success' : score >= 5 ? 'bg-warning' : 'bg-danger';
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-foreground">{label}</span>
        <span className="text-xs font-bold text-foreground">{score}/10</span>
      </div>
      <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden mb-1">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      {note && <p className="text-xs text-muted-foreground leading-relaxed">{note}</p>}
    </div>
  );
}